import React, { PropsWithChildren, useEffect, useState } from "react";
import { useDebounce } from "use-debounce";

import { useApi } from "src/hooks/useApi";

import MenuBar from "src/components/MenuBar";

import {
  DocumentSearch,
  DocumentSearchParams,
  DocumentSearchScopes,
} from "../../components/DocumentSearch";
import Loader from "../../common/Loader";

const DEFAULT_SCOPES: DocumentSearchScopes = {
  slackWorkspace: true,
  platformTeam: true,
};

interface Props<T> {
  typeName: string;
  placeholder: string;
  omitDeleted?: boolean;
  /** Adds the Slack Workspace / Platform Team checkboxes beside Show Deleted. */
  scopeToggles?: boolean;
  /** Heading above the primary table. Omitted when there is only one table. */
  primaryHeading?: string;
  load: (searchParams: DocumentSearchParams) => Promise<T[]>;
  renderTable: (docs: T[]) => React.ReactNode;
  /**
   * Rendered below the primary table. Receives the settled search params with
   * scopes already resolved, so a second table does not re-derive the
   * both-unchecked rule.
   */
  renderExtra?: (params: DocumentSearchParams) => React.ReactNode;
}

export function DocumentSearchPage<T extends { _id: string }>({
  typeName,
  placeholder,
  omitDeleted,
  scopeToggles,
  primaryHeading,
  load,
  renderTable,
  renderExtra,
}: PropsWithChildren<Props<T>>) {
  const [result, setSearch] = useApi<T[], DocumentSearchParams>(load);
  const [searchParams, setSearchParams] = useState<DocumentSearchParams>({
    search: "",
    showDeleted: false,
  });
  const [debouncedSearch] = useDebounce(searchParams.search, 500);
  const [debouncedShowDeleted] = useDebounce(searchParams.showDeleted, 500);
  const [debouncedScopes] = useDebounce(searchParams.scopes, 500);

  // Undefined until the child's first debounce lands. Both boxes start ticked,
  // so defaulting to "on" here keeps the primary table from blinking out on
  // mount; once a value arrives each box is taken literally, and clearing both
  // really does mean "search nothing".
  const scopes = debouncedScopes ?? DEFAULT_SCOPES;
  const slackEnabled = !scopeToggles || scopes.slackWorkspace;
  const platformEnabled = !scopeToggles || scopes.platformTeam;
  const noPopulationSelected = !slackEnabled && !platformEnabled;

  useEffect(() => {
    if (!slackEnabled) {
      return;
    }
    setSearch({ search: debouncedSearch, showDeleted: debouncedShowDeleted });
  }, [debouncedSearch, debouncedShowDeleted, slackEnabled, setSearch]);

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col p-0">
          <MenuBar />
        </div>
      </div>
      <div className="row">
        <div className="col">
          <div className="card mt-2">
            <div className="card-header">
              <h2 className="card-title">{typeName}s</h2>
              <DocumentSearch
                typeName={typeName}
                placeholder={placeholder}
                numFound={slackEnabled ? result.data?.length : undefined}
                omitDeleted={omitDeleted}
                scopeToggles={scopeToggles}
                onSubmit={setSearchParams}
                loading={result.isLoading}
              />
            </div>
          </div>
          {slackEnabled && (
            <>
              {primaryHeading && (
                <h5 className="text-muted mt-3 mb-0">{primaryHeading}</h5>
              )}
              <div className="card bg-dark mt-2">
                {result.isLoading ? (
                  <Loader />
                ) : (
                  <>
                    {result?.data && renderTable(result.data)}
                    {result?.error && (
                      <div className="alert alert-danger">
                        {result.error.toString()}
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
          {noPopulationSelected && (
            <div className="card mt-2">
              <div className="card-body text-muted">
                Tick Slack Workspace or Platform Team to search.
              </div>
            </div>
          )}
          {renderExtra?.({
            search: debouncedSearch,
            showDeleted: debouncedShowDeleted,
            scopes: {
              slackWorkspace: slackEnabled,
              platformTeam: platformEnabled,
            },
          })}
        </div>
      </div>
    </div>
  );
}
